import { useMemo, useState } from 'react'
import { Lock, Search, X } from 'lucide-react'
import {
  Badge,
  Card,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
} from '@/components/ui'
import { ACTIVITY_LABELS, ACTIVITY_ORDER, type ActivityItem, type ActivityKind } from './activityFeed.types'
import { formatActivityDate } from './activityFeed.helpers'

/**
 * Geteilte Verlaufs-Anzeige fuer Ticket-Aktivitaeten.
 *
 * Identisches Layout in der internen Ticket-Detail-Ansicht und im
 * Kundenportal — Style-Aenderungen muessen nur hier gemacht werden.
 *
 * Header („Kommentare, E-Mails, Leistungen (N)") + FilterBar (Suche +
 * Type-Chips) + scrollbare Zeilen-Liste. Klick auf eine Zeile ruft
 * `onItemClick(item)` auf — der Caller rendert das Detail-Modal selbst
 * (siehe `<ActivityDetailModal>`).
 */
export interface TicketActivityFeedProps {
  items: ActivityItem[]
  onItemClick: (item: ActivityItem) => void
  /** Wenn `true`, wird in der Zeile ein „Intern"-Badge gezeigt und die
   *  Zeile leicht eingefaerbt. Im Kundenportal: `false` (interne Eintraege
   *  sind dort schon serverseitig gefiltert). */
  showInternalBadge?: boolean
  /** Optionaler Slot unterhalb der Liste (z. B. „Kommentar schreiben"-Form). */
  footer?: React.ReactNode
  /** Hinweis-Text wenn `items.length === 0` (z. B. „Noch keine Eintraege …"). */
  emptyHint?: React.ReactNode
  className?: string
}

export function TicketActivityFeed({
  items,
  onItemClick,
  showInternalBadge = false,
  footer,
  emptyHint,
  className,
}: TicketActivityFeedProps) {
  const [typeFilter, setTypeFilter] = useState<ActivityKind | 'all'>('all')
  const [searchText, setSearchText] = useState('')

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: items.length }
    for (const a of items) counts[a.kind] = (counts[a.kind] ?? 0) + 1
    return counts
  }, [items])

  const filtered = useMemo(() => {
    let rows = items
    if (typeFilter !== 'all') rows = rows.filter((a) => a.kind === typeFilter)
    const q = searchText.trim().toLowerCase()
    if (q) {
      const terms = q.split(/\s+/)
      rows = rows.filter((a) => {
        const hay = `${a.title} ${a.content} ${a.author ?? ''}`.toLowerCase()
        return terms.every((t) => hay.includes(t))
      })
    }
    return rows
  }, [items, typeFilter, searchText])

  return (
    <Card className={className}>
      <Card.Header className="bg-gray-50">
        <span className="text-small font-semibold text-gray-700">
          Kommentare, E-Mails, Leistungen ({items.length})
        </span>
        <span className="text-caption text-gray-400">Sortierung: absteigend</span>
      </Card.Header>

      {items.length > 0 ? (
        <FilterBar className="rounded-none border-0 border-b shadow-none">
          <Input
            wrapperClassName="flex-1 min-w-[200px]"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Suche durch Leerzeichen getrennt"
            leadingIcon={<Search />}
          />
          {searchText ? (
            <IconButton
              variant="ghost"
              size="sm"
              icon={<X />}
              aria-label="Suche zurücksetzen"
              onClick={() => setSearchText('')}
            />
          ) : null}
          {ACTIVITY_ORDER.filter((k) => (typeCounts[k] ?? 0) > 0).map((kind) => {
            const cfg = ACTIVITY_LABELS[kind]
            const isActive = typeFilter === kind
            const Icon = cfg.Icon
            return (
              <FilterChip
                key={kind}
                active={isActive}
                count={typeCounts[kind] ?? 0}
                leadingIcon={<Icon />}
                onClick={() => setTypeFilter(isActive ? 'all' : kind)}
              >
                {cfg.label}
              </FilterChip>
            )
          })}
        </FilterBar>
      ) : null}

      <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
        {items.length === 0 ? (
          <div className="p-6 text-center text-small text-gray-500">
            {emptyHint ?? 'Noch keine Einträge.'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-small text-gray-400">Keine Einträge</div>
        ) : (
          filtered.map((item) => (
            <ActivityRow
              key={item.id}
              item={item}
              onSelect={onItemClick}
              showInternalBadge={showInternalBadge}
            />
          ))
        )}
      </div>

      {footer ? <div className="border-t border-gray-200">{footer}</div> : null}
    </Card>
  )
}

interface ActivityRowProps {
  item: ActivityItem
  onSelect: (item: ActivityItem) => void
  showInternalBadge: boolean
}

function ActivityRow({ item, onSelect, showInternalBadge }: ActivityRowProps) {
  const cfg = ACTIVITY_LABELS[item.kind]
  const Icon = cfg.Icon
  const dateText = formatActivityDate(item.date)
  const internalHighlight = showInternalBadge && item.internal

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={[
        'w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors',
        internalHighlight ? 'bg-amber-50/50' : '',
      ].join(' ')}
    >
      <Badge variant={cfg.variant} size="sm" className="shrink-0">
        <Icon className="w-3 h-3" />
        {item.badgeLabel || cfg.label}
      </Badge>
      {dateText ? (
        <span className="text-caption text-gray-400 shrink-0 w-[110px]">{dateText}</span>
      ) : null}
      <span className="text-small text-gray-800 font-medium truncate flex-1 min-w-0">
        {item.title || item.content || '—'}
      </span>
      {showInternalBadge && item.internal ? (
        <Badge variant="warning" size="sm" className="shrink-0">
          <Lock className="w-3 h-3" />
          Intern
        </Badge>
      ) : null}
      {item.author ? (
        <span className="text-caption text-gray-500 shrink-0 max-w-[140px] truncate">
          {item.author}
        </span>
      ) : null}
    </button>
  )
}
